/**
 * Auth Context - Provides auth state to React components
 * Uses the unified auth module (src/auth/index.js)
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  loginWithGoogle as authLoginWithGoogle,
  logout as authLogout,
  getCurrentUser,
  onAuthStateChange,
  USE_EMERGENT_AUTH,
  API_URL
} from '../auth';

const AuthContext = createContext(null);

// Public routes that don't need auth
const isPublicRoute = () => {
  const path = window.location.pathname;
  return path.startsWith('/public/') || path.startsWith('/public-');
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      // Skip auth check for public routes
      if (isPublicRoute()) {
        setLoading(false);
        return;
      }
      
      try {
        const currentUser = await getCurrentUser();
        setUser(currentUser);
      } catch (error) {
        console.log('[AuthContext] No active session');
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Skip auth subscription for public routes
    if (isPublicRoute()) {
      return () => {};
    }

    // Subscribe to auth changes (Supabase only)
    const unsubscribe = onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        const currentUser = await getCurrentUser();
        setUser(currentUser);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
      }
    });

    return unsubscribe;
  }, []);

  const signInWithGoogle = useCallback(async () => {
    return await authLoginWithGoogle('/dashboard');
  }, []);

  const signOut = useCallback(async () => {
    await authLogout();
    setUser(null);
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    useEmergentAuth: USE_EMERGENT_AUTH,
    signInWithGoogle,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
